import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { Reorder, useDragControls } from 'framer-motion';
import {
  ArrowDown,
  ArrowLeft,
  ArrowUp,
  ChevronDown,
  ChevronRight,
  Download,
  Eye,
  EyeOff,
  FileText,
  GripVertical,
  Lock,
  RotateCcw,
  Save,
  Ticket,
} from 'lucide-react';
import { getError } from '../../utils';
import rifaService from '../../services/rifa.service';
import { paths } from '../../routes/paths';
import PageHeader from '../../components/shared/PageHeader';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import LoadingBox from '../../components/LoadingBox';
import MessageBox from '../../components/MessageBox';
import CartaEditor from '../../components/rifa/CartaEditor';

const PAGE_W = 794;
const PAGE_H = 1123;
const PINNED = ['header', 'footer'];

/** Editable options per block — mirrors BLOCK_OPTION_SCHEMA in backend/utils/ticketPdfLayout.js */
const OPTION_FIELDS = {
  image: [
    { name: 'height', type: 'select', values: ['S', 'M', 'L', 'auto'] },
    { name: 'showName', type: 'bool' },
  ],
  title: [
    { name: 'showName', type: 'bool' },
    { name: 'showDescription', type: 'bool' },
  ],
  numbers: [{ name: 'size', type: 'select', values: ['S', 'M', 'L', 'list'] }],
  prizes: [
    { name: 'groupBy', type: 'select', values: ['auto', 'sorteo', 'premio'], standardOnly: true },
    { name: 'columns', type: 'select', values: [1, 2, 3, 4], standardOnly: true },
    { name: 'style', type: 'select', values: ['cards', 'list'], standardOnly: true },
    { name: 'showSorteoDate', type: 'bool', standardOnly: true },
    { name: 'showDescription', type: 'bool', standardOnly: true },
  ],
  buyerData: [{ name: 'qrPlacement', type: 'select', values: ['side', 'stacked'] }],
  qr: [
    { name: 'size', type: 'select', values: ['S', 'M', 'L'] },
    { name: 'showAmount', type: 'bool', standardOnly: true },
  ],
};

const selectCls =
  'h-8 w-full appearance-none rounded-md border border-input bg-background pl-2 pr-7 text-xs shadow-sm focus:outline-none focus:ring-2 focus:ring-ring';

const sameLayout = (a, b) => JSON.stringify(a?.blocks || []) === JSON.stringify(b?.blocks || []);
const sameCarta = (a, b) => JSON.stringify(a || {}) === JSON.stringify(b || {});

function downloadBlob(data, filename) {
  const blob = new Blob([data], { type: 'application/pdf' });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
}

function BlockOptions({ block, isBoard, onChange }) {
  const { t } = useTranslation();
  const fields = (OPTION_FIELDS[block.key] || []).filter(f => !(isBoard && f.standardOnly));
  if (!fields.length) {
    return <p className='text-xs text-muted-foreground'>{t('ticketDesign.noOptions')}</p>;
  }
  return (
    <div className='grid gap-3 sm:grid-cols-2'>
      {fields.map(field => {
        const value = block.options?.[field.name];
        const label = t(`ticketDesign.opt.${block.key}.${field.name}`);
        if (field.type === 'bool') {
          return (
            <label key={field.name} className='flex items-center gap-2 text-xs text-foreground'>
              <input
                type='checkbox'
                className='h-4 w-4 rounded border-input'
                checked={!!value}
                onChange={e => onChange({ ...block.options, [field.name]: e.target.checked })}
              />
              {label}
            </label>
          );
        }
        return (
          <label key={field.name} className='space-y-1 text-xs text-muted-foreground'>
            <span>{label}</span>
            <select
              className={selectCls}
              value={value}
              onChange={e => {
                const raw = e.target.value;
                const v = typeof field.values[0] === 'number' ? Number(raw) : raw;
                onChange({ ...block.options, [field.name]: v });
              }}
            >
              {field.values.map(v => (
                <option key={v} value={v}>
                  {typeof v === 'number' ? v : t(`ticketDesign.val.${block.key}.${v}`)}
                </option>
              ))}
            </select>
          </label>
        );
      })}
    </div>
  );
}

function BlockRow({
  block,
  index,
  total,
  isBoard,
  expanded,
  onToggleExpand,
  onChange,
  onMove,
  dragControls,
}) {
  const { t } = useTranslation();
  const pinned = PINNED.includes(block.key);
  return (
    <div
      className={`rounded-lg border bg-card ${block.visible ? 'border-border' : 'border-dashed border-border opacity-60'}`}
    >
      <div className='flex items-center gap-2 px-2 py-2'>
        {pinned ? (
          <span
            className='flex h-8 w-6 items-center justify-center text-muted-foreground'
            title={t('ticketDesign.pinned')}
          >
            <Lock className='h-3.5 w-3.5' />
          </span>
        ) : (
          <span
            className='flex h-8 w-6 cursor-grab touch-none items-center justify-center text-muted-foreground active:cursor-grabbing'
            onPointerDown={e => dragControls?.start(e)}
            title={t('ticketDesign.dragHint')}
          >
            <GripVertical className='h-4 w-4' />
          </span>
        )}
        <button
          type='button'
          className='flex min-w-0 flex-1 items-center gap-1 text-left text-sm font-medium text-foreground'
          onClick={onToggleExpand}
        >
          {expanded ? (
            <ChevronDown className='h-4 w-4 shrink-0' />
          ) : (
            <ChevronRight className='h-4 w-4 shrink-0' />
          )}
          <span className='truncate'>{t(`ticketDesign.block.${block.key}`)}</span>
          {pinned && (
            <Badge variant='secondary' className='ml-1 shrink-0'>
              {block.key === 'header'
                ? t('ticketDesign.pinnedTop')
                : t('ticketDesign.pinnedBottom')}
            </Badge>
          )}
        </button>
        {!pinned && (
          <>
            <Button
              type='button'
              variant='ghost'
              size='icon-sm'
              disabled={index === 0}
              onClick={() => onMove(-1)}
              title={t('ticketDesign.moveUp')}
            >
              <ArrowUp className='h-4 w-4' />
            </Button>
            <Button
              type='button'
              variant='ghost'
              size='icon-sm'
              disabled={index === total - 1}
              onClick={() => onMove(1)}
              title={t('ticketDesign.moveDown')}
            >
              <ArrowDown className='h-4 w-4' />
            </Button>
          </>
        )}
        <Button
          type='button'
          variant='ghost'
          size='icon-sm'
          onClick={() => onChange({ ...block, visible: !block.visible })}
          title={block.visible ? t('ticketDesign.hide') : t('ticketDesign.show')}
        >
          {block.visible ? <Eye className='h-4 w-4' /> : <EyeOff className='h-4 w-4' />}
        </Button>
      </div>
      {expanded && (
        <div className='border-t border-border px-3 py-3'>
          <BlockOptions
            block={block}
            isBoard={isBoard}
            onChange={options => onChange({ ...block, options })}
          />
        </div>
      )}
    </div>
  );
}

/** Reorder.Item wrapper so each row gets its own drag controls (drag only from the grip). */
function DraggableBlock(props) {
  const dragControls = useDragControls();
  return (
    <Reorder.Item
      value={props.block.key}
      dragListener={false}
      dragControls={dragControls}
      className='list-none'
    >
      <BlockRow {...props} dragControls={dragControls} />
    </Reorder.Item>
  );
}

export default function RifaTicketDesigner() {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [rifa, setRifa] = useState(null);
  const [layout, setLayout] = useState(null);
  const [savedLayout, setSavedLayout] = useState(null);
  const [defaultLayout, setDefaultLayout] = useState(null);
  // PDF format: 'boleto' (block layout) | 'carta' (letter)
  const [format, setFormat] = useState('boleto');
  const [savedFormat, setSavedFormat] = useState('boleto');
  const [carta, setCarta] = useState(null);
  const [savedCarta, setSavedCarta] = useState(null);
  const [defaultCarta, setDefaultCarta] = useState(null);
  const [imagenLateralUrl, setImagenLateralUrl] = useState('');
  const [expanded, setExpanded] = useState({});
  const [sampleCount, setSampleCount] = useState(3);
  const [previewHtml, setPreviewHtml] = useState('');
  const [previewLoading, setPreviewLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [downloading, setDownloading] = useState(false);

  const previewBoxRef = useRef(null);
  const [previewWidth, setPreviewWidth] = useState(400);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        const [rifaData, layoutData] = await Promise.all([
          rifaService.getRifa(id),
          rifaService.getPdfLayout(id),
        ]);
        if (cancelled) return;
        setRifa(rifaData);
        setLayout(layoutData.layout);
        setSavedLayout(layoutData.layout);
        setDefaultLayout(layoutData.defaultLayout);
        const fmt = layoutData.format === 'carta' ? 'carta' : 'boleto';
        setFormat(fmt);
        setSavedFormat(fmt);
        setCarta(layoutData.carta);
        setSavedCarta(layoutData.carta);
        setDefaultCarta(layoutData.defaultCarta);
        setImagenLateralUrl(layoutData.imagenLateralUrl || '');
      } catch (err) {
        if (!cancelled) setError(getError(err));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [id]);

  // Scale the A4 iframe to the available width
  useEffect(() => {
    const el = previewBoxRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(entries => {
      const w = entries[0]?.contentRect?.width;
      if (w) setPreviewWidth(w);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, [loading]);

  const clampedCount = Math.max(1, Math.min(500, Number(sampleCount) || 1));
  const isCarta = format === 'carta';
  // Unsaved document sent to the preview / save endpoints (blocks + format + letter)
  const pdfDocument = useMemo(
    () => (layout ? { ...layout, format, ...(carta ? { carta } : {}) } : null),
    [layout, format, carta]
  );

  // Debounced live preview (HTML rendered by the real template, auto-fit runs inside the iframe)
  useEffect(() => {
    if (!pdfDocument) return undefined;
    const controller = new AbortController();
    const timer = setTimeout(async () => {
      try {
        setPreviewLoading(true);
        const html = await rifaService.previewPdfLayoutHtml(
          id,
          { pdfLayout: pdfDocument, sampleCount: clampedCount },
          { signal: controller.signal }
        );
        setPreviewHtml(html);
      } catch (err) {
        if (!controller.signal.aborted)
          toast.error(getError(err) || t('ticketDesign.previewError'));
      } finally {
        if (!controller.signal.aborted) setPreviewLoading(false);
      }
    }, 400);
    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [id, pdfDocument, clampedCount, t]);

  const isBoard = rifa?.tipo === 'board';
  const header = layout?.blocks.find(b => b.key === 'header');
  const footer = layout?.blocks.find(b => b.key === 'footer');
  const bodyBlocks = useMemo(
    () => (layout?.blocks || []).filter(b => !PINNED.includes(b.key)),
    [layout]
  );
  const dirty =
    layout &&
    savedLayout &&
    (!sameLayout(layout, savedLayout) || format !== savedFormat || !sameCarta(carta, savedCarta));
  const isDefault = layout && defaultLayout && sameLayout(layout, defaultLayout);
  const isDefaultCarta = carta && defaultCarta && sameCarta(carta, defaultCarta);

  const setBody = useCallback(
    nextBody =>
      setLayout(prev => ({
        blocks: [prev.blocks[0], ...nextBody, prev.blocks[prev.blocks.length - 1]],
      })),
    []
  );

  const updateBlock = useCallback(
    updated =>
      setLayout(prev => ({ blocks: prev.blocks.map(b => (b.key === updated.key ? updated : b)) })),
    []
  );

  const handleReorder = keys => {
    const byKey = Object.fromEntries(bodyBlocks.map(b => [b.key, b]));
    setBody(keys.map(k => byKey[k]));
  };

  const moveBlock = (index, dir) => {
    const next = [...bodyBlocks];
    const target = index + dir;
    if (target < 0 || target >= next.length) return;
    [next[index], next[target]] = [next[target], next[index]];
    setBody(next);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      // Saving the default design stores "no custom layout" so future default tweaks apply
      const payload = {
        blocks: isDefault ? [] : layout.blocks,
        format,
        ...(carta ? { carta } : {}),
      };
      const data = await rifaService.savePdfLayout(id, payload);
      setLayout(data.layout);
      setSavedLayout(data.layout);
      const fmt = data.format === 'carta' ? 'carta' : 'boleto';
      setFormat(fmt);
      setSavedFormat(fmt);
      setCarta(data.carta);
      setSavedCarta(data.carta);
      setImagenLateralUrl(data.imagenLateralUrl || '');
      toast.success(t('ticketDesign.saved'));
    } catch (err) {
      toast.error(getError(err) || t('ticketDesign.saveError'));
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    if (isCarta) {
      if (defaultCarta) setCarta(defaultCarta);
      setImagenLateralUrl('');
      toast.info(t('ticketDesign.carta.resetHint'));
      return;
    }
    if (defaultLayout) setLayout(defaultLayout);
    toast.info(t('ticketDesign.resetHint'));
  };

  const handleDownload = async () => {
    setDownloading(true);
    try {
      const data = await rifaService.previewPdfLayoutPdf(id, {
        pdfLayout: pdfDocument,
        sampleCount: clampedCount,
      });
      downloadBlob(data, isCarta ? 'carta-prueba.pdf' : 'boleto-prueba.pdf');
    } catch (err) {
      toast.error(getError(err) || t('ticketDesign.previewError'));
    } finally {
      setDownloading(false);
    }
  };

  if (loading) return <LoadingBox />;
  if (error) return <MessageBox variant='danger'>{error}</MessageBox>;
  if (!layout) return null;

  const scale = Math.min(1, previewWidth / PAGE_W);
  const rowProps = key => ({
    expanded: !!expanded[key],
    onToggleExpand: () => setExpanded(prev => ({ ...prev, [key]: !prev[key] })),
    onChange: updateBlock,
    isBoard,
  });

  return (
    <div className='space-y-6'>
      <PageHeader
        title={isCarta ? t('ticketDesign.carta.title') : t('ticketDesign.title')}
        subtitle={
          rifa?.nombre ? `${t('raffle.raffle')} — ${rifa.nombre}` : t('ticketDesign.subtitle')
        }
        actions={
          <div className='flex flex-wrap gap-2'>
            <Button variant='outline' onClick={() => navigate(paths.admin.editRifa(id))}>
              <ArrowLeft className='h-4 w-4' />
              {t('common.back')}
            </Button>
            <Button
              variant='outline'
              onClick={handleReset}
              disabled={isCarta ? isDefaultCarta : isDefault}
            >
              <RotateCcw className='h-4 w-4' />
              {t('ticketDesign.reset')}
            </Button>
            <Button onClick={handleSave} disabled={saving || !dirty}>
              <Save className='h-4 w-4' />
              {t('ticketDesign.save')}
            </Button>
          </div>
        }
      />

      {/* Format switch: ticket (blocks) or letter */}
      <div className='space-y-1.5'>
        <div
          role='radiogroup'
          aria-label={t('ticketDesign.format.label')}
          className='inline-flex rounded-lg border border-border bg-muted p-1'
        >
          {[
            { value: 'boleto', Icon: Ticket },
            { value: 'carta', Icon: FileText },
          ].map(({ value, Icon }) => (
            <button
              key={value}
              type='button'
              role='radio'
              aria-checked={format === value}
              onClick={() => setFormat(value)}
              className={`flex items-center gap-2 rounded-md px-4 py-1.5 text-sm font-medium transition-colors ${
                format === value
                  ? 'bg-background text-foreground shadow-sm'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              <Icon className='h-4 w-4' />
              {t(`ticketDesign.format.${value}`)}
            </button>
          ))}
        </div>
        <p className='text-xs text-muted-foreground'>
          {isCarta ? t('ticketDesign.format.cartaHint') : t('ticketDesign.format.boletoHint')}
        </p>
      </div>

      <div className='grid gap-6 lg:grid-cols-[minmax(0,420px)_minmax(0,1fr)]'>
        {/* Left: letter form */}
        {isCarta && carta && (
          <Card className='h-fit'>
            <CardHeader>
              <CardTitle className='flex items-center justify-between gap-2'>
                <span>{t('ticketDesign.carta.form')}</span>
                {dirty && <Badge variant='warning'>{t('ticketDesign.unsaved')}</Badge>}
              </CardTitle>
              <p className='text-xs text-muted-foreground'>{t('ticketDesign.carta.formHint')}</p>
            </CardHeader>
            <CardContent>
              <CartaEditor
                carta={carta}
                onChange={setCarta}
                imagenLateralUrl={imagenLateralUrl}
                onImagenLateralUrl={setImagenLateralUrl}
              />
              <p className='pt-4 text-xs text-muted-foreground'>
                {t('ticketDesign.carta.autoFitHint')}
              </p>
            </CardContent>
          </Card>
        )}

        {/* Left: block list */}
        <Card className={isCarta ? 'hidden' : 'h-fit'}>
          <CardHeader>
            <CardTitle className='flex items-center justify-between gap-2'>
              <span>{t('ticketDesign.blocks')}</span>
              {dirty && <Badge variant='warning'>{t('ticketDesign.unsaved')}</Badge>}
            </CardTitle>
            <p className='text-xs text-muted-foreground'>{t('ticketDesign.blocksHint')}</p>
          </CardHeader>
          <CardContent className='space-y-2'>
            {header && <BlockRow block={header} index={0} total={1} {...rowProps('header')} />}
            <Reorder.Group
              axis='y'
              values={bodyBlocks.map(b => b.key)}
              onReorder={handleReorder}
              className='space-y-2'
            >
              {bodyBlocks.map((block, index) => (
                <DraggableBlock
                  key={block.key}
                  block={block}
                  index={index}
                  total={bodyBlocks.length}
                  onMove={dir => moveBlock(index, dir)}
                  {...rowProps(block.key)}
                />
              ))}
            </Reorder.Group>
            {footer && <BlockRow block={footer} index={0} total={1} {...rowProps('footer')} />}
            <p className='pt-2 text-xs text-muted-foreground'>{t('ticketDesign.autoFitHint')}</p>
          </CardContent>
        </Card>

        {/* Right: live preview */}
        <Card className='h-fit'>
          <CardHeader>
            <div className='flex flex-wrap items-end justify-between gap-3'>
              <CardTitle>{t('ticketDesign.preview')}</CardTitle>
              <div className='flex flex-wrap items-end gap-2'>
                <label className='space-y-1 text-xs text-muted-foreground'>
                  <span className='block'>{t('ticketDesign.simulateNumbers')}</span>
                  <input
                    type='number'
                    min={1}
                    max={500}
                    className='h-9 w-24 rounded-md border border-input bg-background px-2 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-ring'
                    value={sampleCount}
                    onChange={e => setSampleCount(e.target.value)}
                  />
                </label>
                <Button variant='outline' size='sm' onClick={handleDownload} disabled={downloading}>
                  <Download className='h-4 w-4' />
                  {downloading ? t('ticketDesign.downloading') : t('ticketDesign.downloadTest')}
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div ref={previewBoxRef} className='mx-auto w-full max-w-[794px]'>
              <div
                className='relative overflow-hidden rounded-md border border-border bg-white shadow-sm'
                style={{ height: PAGE_H * scale }}
              >
                {previewHtml && (
                  <iframe
                    title={t('ticketDesign.preview')}
                    srcDoc={previewHtml}
                    sandbox='allow-scripts'
                    style={{
                      width: PAGE_W,
                      height: PAGE_H,
                      border: 0,
                      transform: `scale(${scale})`,
                      transformOrigin: 'top left',
                    }}
                  />
                )}
                {previewLoading && (
                  <div className='absolute right-2 top-2 rounded bg-black/60 px-2 py-1 text-xs text-white'>
                    {t('ticketDesign.updating')}
                  </div>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
